import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';

export interface Project {
  id: string;
  title: string;
  description: string;
  created_at: string;
  created_by: string;
  start_date: string;
  end_date: string;
  status: string;
  template_id: string;
  updated_at?: string;
  progress?: number;
  members: {
    id: string;
    role: string;
    email: string;
    status: string;
    avatar_url?: string;
    full_name?: string;
  }[] | null;
  is_research?: boolean;
  assignedClients?: {
    id: string;
    full_name: string;
    company: string | null;
    email: string;
  }[];
  client?: string;
  report_data?: any;
  graph_data?: any;
}

interface ProjectsContextType {
  activeProjects: Project[];
  archivedProjects: Project[];
  isLoading: boolean;
  currentProjectId: string | null;
  setCurrentProjectId: (id: string | null) => void;
  getProjectTitle: (id: string) => string | null;
  refreshProjects: () => Promise<void>;
}

const ProjectsContext = createContext<ProjectsContextType | undefined>(undefined);

export function ProjectsProvider({ children }: { children: React.ReactNode }) {
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(() => {
    // Load from localStorage on initial render
    return localStorage.getItem('currentProjectId');
  });
  const { user } = useAuth();
  const [userProfile, setUserProfile] = useState<{ role?: string } | null>(null);
  const queryClient = useQueryClient();

  // Fetch user profile to determine access rights
  const { data: userProfileData, isLoading: isUserProfileLoading } = useQuery({
    queryKey: ['userProfile', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  useEffect(() => {
    if (userProfileData) {
      setUserProfile(userProfileData);
    }
  }, [userProfileData]);

  // Memoized query function to prevent unnecessary re-executions
  const projectsQueryFn = useCallback(async () => {
    if (!user) {
      return { active: [], archived: [] };
    }
    try {
      let allUserProjects: (Omit<Project, 'progress'>)[] = [];

      // Get all non-research projects first, then filter for user access
      const { data: projects, error } = await supabase
        .from('projects')
        .select('*')
        .eq('is_research', false)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Filter projects where user is creator or active member using project_members table
      const userProjectIds = new Set<string>();

      // Always include projects created by the user
      (projects || []).forEach(project => {
        if (project.created_by === user.id) {
          userProjectIds.add(project.id);
        }
      });

      // Get project memberships for this user
      const { data: memberships, error: membershipError } = await supabase
        .from('project_members')
        .select('project_id')
        .eq('user_id', user.id)
        .eq('status', 'active');

      if (!membershipError && memberships) {
        memberships.forEach(membership => {
          userProjectIds.add(membership.project_id);
        });
      }

      // Filter projects to only include those the user has access to
      const accessibleProjects = (projects || []).filter(project => userProjectIds.has(project.id));

      // Fetch member data for all accessible projects
      if (accessibleProjects.length > 0) {
        const projectIds = accessibleProjects.map(p => p.id);
        const { data: allMemberships, error: membershipsError } = await supabase
          .from('project_members')
          .select(`
              project_id,
              user_id,
              role,
              status
            `)
          .in('project_id', projectIds)
          .eq('status', 'active');

        // Fetch profiles separately for all member user_ids
        let profilesMap: Record<string, any> = {};
        if (!membershipsError && allMemberships && allMemberships.length > 0) {
          const userIds = [...new Set(allMemberships.map(m => m.user_id))];
          const { data: profiles, error: profilesError } = await supabase
            .from('profiles')
            .select('id, first_name, last_name, email, avatar_url')
            .in('id', userIds);

          if (!profilesError && profiles) {
            profilesMap = profiles.reduce((acc, profile) => {
              acc[profile.id] = profile;
              return acc;
            }, {} as Record<string, any>);
          }
        }

        if (!membershipsError && allMemberships) {
          // Group memberships by project_id
          const membershipsByProject = allMemberships.reduce((acc, membership) => {
            if (!acc[membership.project_id]) {
              acc[membership.project_id] = [];
            }
            acc[membership.project_id].push(membership);
            return acc;
          }, {} as Record<string, typeof allMemberships>);

          // Transform projects with member data
          allUserProjects = accessibleProjects.map(project => ({
            ...project,
            // Transform memberships into the expected members format for UI compatibility
            members: (membershipsByProject[project.id] || [])
              .filter(member => member.status === 'active')
              .map(member => {
                const profile = profilesMap[member.user_id];
                return {
                  id: member.user_id,
                  role: member.role,
                  email: profile?.email || '',
                  status: member.status,
                  full_name: profile ?
                    `${profile.first_name || ''} ${profile.last_name || ''}`.trim() :
                    '',
                  avatar_url: profile?.avatar_url || null
                };
              })
          }));
        } else {
          // If member fetch fails, just use projects without members
          allUserProjects = accessibleProjects.map(project => ({
            ...project,
            members: []
          }));
        }
      }

      // For clients, also check if they are associated with projects through the clients table
      if (userProfile?.role === 'client') {
        const { data: clientData, error: clientError } = await supabase
          .from('clients')
          .select('current_projects')
          .eq('email', user.email)
          .single();

        if (!clientError && clientData?.current_projects && clientData.current_projects.length > 0) {
          // Get projects that the client is associated with
          const { data: clientProjects, error: clientProjectsError } = await supabase
            .from('projects')
            .select('*')
            .eq('is_research', false)
            .in('id', clientData.current_projects)
            .order('created_at', { ascending: false });

          if (!clientProjectsError && clientProjects) {
            // Fetch member data for client projects
            const clientProjectIds = clientProjects.map(p => p.id);
            if (clientProjectIds.length > 0) {
              const { data: clientMemberships, error: clientMembershipsError } = await supabase
                .from('project_members')
                .select(`
                    project_id,
                    user_id,
                    role,
                    status
                  `)
                .in('project_id', clientProjectIds)
                .eq('status', 'active');

              // Fetch profiles separately for client project members
              let clientProfilesMap: Record<string, any> = {};
              if (!clientMembershipsError && clientMemberships && clientMemberships.length > 0) {
                const clientUserIds = [...new Set(clientMemberships.map(m => m.user_id))];
                const { data: clientProfiles, error: clientProfilesError } = await supabase
                  .from('profiles')
                  .select('id, first_name, last_name, email, avatar_url')
                  .in('id', clientUserIds);

                if (!clientProfilesError && clientProfiles) {
                  clientProfilesMap = clientProfiles.reduce((acc, profile) => {
                    acc[profile.id] = profile;
                    return acc;
                  }, {} as Record<string, any>);
                }
              }

              // Group client memberships by project_id
              const clientMembershipsByProject = (clientMemberships || []).reduce((acc, membership) => {
                if (!acc[membership.project_id]) {
                  acc[membership.project_id] = [];
                }
                acc[membership.project_id].push(membership);
                return acc;
              }, {} as Record<string, typeof clientMemberships>);

              // Transform client projects with member data
              const transformedClientProjects = clientProjects.map(project => ({
                ...project,
                // Transform memberships into the expected members format for UI compatibility
                members: (clientMembershipsByProject[project.id] || [])
                  .filter(member => member.status === 'active')
                  .map(member => {
                    const profile = clientProfilesMap[member.user_id];
                    return {
                      id: member.user_id,
                      role: member.role,
                      email: profile?.email || '',
                      status: member.status,
                      full_name: profile ?
                        `${profile.first_name || ''} ${profile.last_name || ''}`.trim() :
                        '',
                      avatar_url: profile?.avatar_url || null
                    };
                  })
              }));

              // Add client projects, avoiding duplicates
              const existingProjectIds = new Set(allUserProjects.map(p => p.id));
              const newClientProjects = transformedClientProjects.filter(p => !existingProjectIds.has(p.id));
              allUserProjects = [...allUserProjects, ...newClientProjects];
            } else {
              // Add client projects without members if no memberships found
              const existingProjectIds = new Set(allUserProjects.map(p => p.id));
              const newClientProjects = clientProjects.filter(p => !existingProjectIds.has(p.id));
              allUserProjects = [...allUserProjects, ...newClientProjects.map(p => ({ ...p, members: [] }))];
            }
          }
        }
      }

      // Process all projects to add progress and member details
      // Optimization: Batch fetch data for all projects at once instead of N+1 queries
      const allProjectIds = allUserProjects.map(p => p.id);
      const allMemberIds = Array.from(new Set(allUserProjects.flatMap(p => p.members?.map(m => m.id) || [])));

      const [mapsResult, subtasksResult, profilesResult] = await Promise.all([
        // Batch fetch maps
        supabase
          .from('project_maps')
          .select('project_id, flow_map')
          .in('project_id', allProjectIds),
        // Batch fetch subtasks
        supabase
          .from('subtasks')
          .select('project_id, is_completed')
          .in('project_id', allProjectIds),
        // Batch fetch profiles
        allMemberIds.length > 0 ? supabase
          .from('profiles')
          .select('id, avatar_url, first_name, last_name')
          .in('id', allMemberIds) : Promise.resolve({ data: [], error: null })
      ]);

      // Create fast lookup maps
      const projectMaps = new Map<string, any>(mapsResult.data?.map((m: any) => [m.project_id, m.flow_map] as [string, any]) || []);

      // Group subtasks by project_id
      const subtasksByProject = new Map<string, any[]>();
      subtasksResult.data?.forEach((s: any) => {
        const current = subtasksByProject.get(s.project_id) || [];
        current.push(s);
        subtasksByProject.set(s.project_id, current);
      });

      const profileMap = new Map<string, any>(profilesResult.data?.map((p: any) => [p.id, p] as [string, any]) || []);

      // Process all projects in memory
      const processedProjects = allUserProjects.map((project) => {
        let progress = 0;
        let enhancedMembers = project.members || [];

        try {
          // Calculate Progress
          const flowMap = projectMaps.get(project.id) as any;

          if (flowMap?.mapNodes?.length > 0) {
            const taskNodes = flowMap.mapNodes.filter(
              (node: any) => node.nodeType === 'text' || node.nodeType === 'file' || node.nodeType === 'image' || !node.nodeType
            );

            let totalItems = taskNodes.length;
            let completedItems = taskNodes.filter((node: any) => node.isCompleted === true).length;

            const subtasks = subtasksByProject.get(project.id) || [];
            totalItems += subtasks.length;
            completedItems += subtasks.filter((s: any) => s.is_completed).length;

            if (totalItems > 0) {
              progress = Math.round((completedItems / totalItems) * 100);
            }
          }

          // Enhance Members
          if (project.members && project.members.length > 0) {
            enhancedMembers = project.members.map(member => {
              const profile = profileMap.get(member.id);
              // Use existing full_name if available, otherwise construct from profile
              let fullName = member.full_name;
              if (!fullName && profile) {
                fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
              }
              if (!fullName || fullName.trim() === '') {
                fullName = member.email;
              }
              return {
                ...member,
                avatar_url: profile?.avatar_url || member.avatar_url,
                full_name: fullName
              };
            });
          }
        } catch (e) {
          console.error(`Failed to process data for project ${project.id}:`, e);
        }
        return { ...project, progress, members: enhancedMembers };
      });

      // Separate active and archived projects
      const active = processedProjects.filter(p => p.status === 'active' || !p.status);
      const archived = processedProjects.filter(p => p.status === 'archived');

      active.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      archived.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      return { active, archived };
    } catch (error) {
      console.error('Error fetching projects:', error);
      return { active: [], archived: [] };
    }
  }, [user, userProfile]);

  // Fetch projects with React Query
  const { data: processedProjects = { active: [], archived: [] }, isLoading, refetch } = useQuery<{ active: Project[]; archived: Project[] }>({
    queryKey: ['projects', user?.id, userProfile?.role],
    queryFn: projectsQueryFn,
    enabled: !!user && !!userProfile,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  // Memoize active and archived projects to prevent unnecessary re-renders
  const activeProjects = useMemo(() => processedProjects.active as Project[], [processedProjects.active]);
  const archivedProjects = useMemo(() => processedProjects.archived as Project[], [processedProjects.archived]);

  // Real-time subscriptions: invalidate query instead of calling fetchProjects
  useEffect(() => {
    if (!user || !userProfile) return;
    // Set up real-time subscription for project changes
    const channel = supabase
      .channel('projects-context')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'projects',
          filter: `is_research=eq.false`
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['projects', user?.id, userProfile?.role] });
        }
      )
      .subscribe();
    // Listen for project deletion events
    const handleProjectDeleted = (event: CustomEvent<{ projectId: string }>) => {
      if (event.detail.projectId === currentProjectId) {
        setCurrentProjectId(null);
      }
      queryClient.invalidateQueries({ queryKey: ['projects', user?.id, userProfile?.role] });
    };
    window.addEventListener('projectDeleted', handleProjectDeleted as EventListener);
    // Also subscribe to project map changes to update progress
    const projectMapsSubscription = supabase
      .channel('project-maps-context-changes')
      .on('postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_maps'
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['projects', user?.id, userProfile?.role] });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
      supabase.removeChannel(projectMapsSubscription);
      window.removeEventListener('projectDeleted', handleProjectDeleted as EventListener);
    };
  }, [user, userProfile, currentProjectId, queryClient]);

  // Save current project ID to localStorage whenever it changes
  useEffect(() => {
    if (currentProjectId) {
      localStorage.setItem('currentProjectId', currentProjectId);
    } else {
      localStorage.removeItem('currentProjectId');
    }
  }, [currentProjectId]);

  // Ensure currentProjectId is always valid if projects are available
  useEffect(() => {
    // If there are active projects and currentProjectId is not set or invalid, set it to the first active project
    if (
      activeProjects.length > 0 &&
      (!currentProjectId || ![...activeProjects, ...archivedProjects].some(p => p.id === currentProjectId))
    ) {
      setCurrentProjectId(activeProjects[0].id);
    }
  }, [activeProjects, archivedProjects, currentProjectId]);

  // Helper function to get project title by ID
  const getProjectTitle = (id: string): string | null => {
    const project = [...activeProjects, ...archivedProjects].find(p => p.id === id);
    return project ? project.title : null;
  };

  // Refresh function for manual updates
  const refreshProjects = async () => {
    await refetch();
  };

  return (
    <ProjectsContext.Provider value={{
      activeProjects,
      archivedProjects,
      isLoading,
      currentProjectId,
      setCurrentProjectId,
      getProjectTitle,
      refreshProjects
    }}>
      {children}
    </ProjectsContext.Provider>
  );
}

export function useProjects() {
  const context = useContext(ProjectsContext);
  if (context === undefined) {
    throw new Error('useProjects must be used within a ProjectsProvider');
  }
  return context;
} 